// Utility functions for generating and handling SEO-friendly URL slugs

/**
 * Generate a URL-safe slug from product name
 * @param {string} name - Product name
 * @returns {string} URL-safe slug
 */
export const generateSlug = (name) => {
  if (!name) return '';
  
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Generate product URL with slug and ID
 * @param {object} product - Product object with id and name
 * @returns {string} SEO-friendly URL
 */
export const generateProductUrl = (product) => {
  if (!product) return '/products';
  
  const productId = product.docId || product.id;
  if (!productId) return '/products';
  
  const slug = generateSlug(product.name || 'product');
  return `/products/${slug}-${productId}`;
};

/**
 * Extract product ID from slug-based URL
 * @param {string} slugParam - URL parameter (e.g., "photo-lamp-abc123" or just "abc123")
 * @returns {string} Product ID
 */
export const extractProductId = (slugParam) => {
  if (!slugParam) return null;
  
  // If it contains a hyphen, extract the last part (ID)
  if (slugParam.includes('-')) {
    const parts = slugParam.split('-');
    return parts[parts.length - 1];
  }
  
  // Otherwise, it's just the ID (backwards compatibility)
  return slugParam;
};

/**
 * Validate if a slug-ID combination is correct for a product
 * @param {string} slugParam - URL parameter
 * @param {object} product - Product object
 * @returns {boolean} True if slug matches product name
 */
export const isCorrectSlug = (slugParam, product) => {
  if (!slugParam || !product) return false;
  
  const expectedUrl = generateProductUrl(product);
  const actualUrl = `/products/${slugParam}`;
  
  return expectedUrl === actualUrl;
};
